import { useNavigate, useParams } from "@tanstack/solid-router";
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { For, Show, createMemo, createSignal } from "solid-js";
import { Avatar } from "~/components/Avatar";
import { ProjectCard } from "~/components/ProjectCard";
import { SkillChips } from "~/components/SkillChips";
import { ConnectModal } from "~/components/ConnectModal";
import { OpenToBadges } from "~/components/OpenToBadges";
import { track } from "~/lib/analytics";
import {
  cancelConnection,
  connectionWithPeer,
  createConnection,
  fetchConnections,
  fetchProfile,
  fetchProfileDetail,
  reportProfile,
  respondConnection,
} from "~/lib/api";
import { connectionsQueryKey, profileQueryKey } from "~/lib/routes";

export function ProfileDetailPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const eventId = () => params().eventId ?? "";
  const profileId = () => params().profileId ?? "";

  const [connectOpen, setConnectOpen] = createSignal(false);
  const [connectError, setConnectError] = createSignal("");

  const detailQuery = createQuery(() => ({
    queryKey: ["profile-detail", eventId() || "all", profileId()],
    queryFn: () => fetchProfileDetail(profileId(), eventId() || undefined),
    enabled: !!profileId(),
  }));

  const myProfileQuery = createQuery(() => ({
    queryKey: profileQueryKey(eventId()),
    queryFn: () => fetchProfile(eventId() || undefined),
  }));

  const connectionsQuery = createQuery(() => ({
    queryKey: connectionsQueryKey(),
    queryFn: () => fetchConnections(),
  }));

  const connection = createMemo(() => connectionWithPeer(connectionsQuery.data?.data, profileId()));
  const isOwnProfile = createMemo(
    () => myProfileQuery.data?.data?.user_id === profileId(),
  );

  function goDirectory() {
    if (eventId()) {
      navigate({ to: "/directory/$eventId", params: { eventId: eventId() } });
      return;
    }
    navigate({ to: "/directory" });
  }

  function refreshConnections() {
    queryClient.invalidateQueries({ queryKey: ["connections"] });
  }

  const connectMutation = createMutation(() => ({
    mutationFn: (note: string) => createConnection(profileId(), eventId() || undefined, note),
    onSuccess: () => {
      setConnectOpen(false);
      setConnectError("");
      refreshConnections();
    },
    onError: (err: unknown) => setConnectError((err as Error).message),
  }));

  const respondMutation = createMutation(() => ({
    mutationFn: ({ id, action }: { id: string; action: "accept" | "decline" }) =>
      respondConnection(id, action),
    onSuccess: () => refreshConnections(),
  }));

  const cancelMutation = createMutation(() => ({
    mutationFn: (id: string) => cancelConnection(id),
    onSuccess: () => refreshConnections(),
  }));

  const reportMutation = createMutation(() => ({
    mutationFn: (reason: string) => reportProfile(profileId(), eventId() || undefined, reason),
    onSuccess: () => alert("Report submitted. The team will review it."),
    onError: (err: unknown) => alert((err as Error).message),
  }));

  function onReport() {
    const reason = window.prompt("Why are you reporting this profile? (required)");
    if (!reason?.trim()) return;
    reportMutation.mutate(reason.trim());
  }

  return (
    <section class="card">
      <Show when={detailQuery.isLoading}>
        <p class="muted">Loading profile…</p>
      </Show>
      <Show when={detailQuery.isError}>
        <div class="error-box">{(detailQuery.error as Error).message}</div>
        <button type="button" class="btn btn-ghost" onClick={goDirectory}>
          Back to directory
        </button>
      </Show>
      <Show when={detailQuery.data} keyed>
        {(res) => {
          const p = res.data;
          const conn = () => connection();
          return (
            <>
              <div class="profile-detail-header">
                <Avatar url={p.avatar_url} name={p.display_name} size="lg" />
                <div>
                  <h1>{p.display_name}</h1>
                  <p class="profile-preview-headline">{p.headline ?? p.job_role}</p>
                  <p class="muted">{p.company}</p>
                </div>
              </div>

              <Show when={p.bio}>
                <p class="profile-detail-bio">{p.bio}</p>
              </Show>
              <Show when={p.looking_for}>
                <p class="looking-for">
                  <span class="status-chip">Looking for</span> {p.looking_for}
                </p>
              </Show>
              <OpenToBadges flags={p} />

              <SkillChips skills={p.skills} />
              <Show when={p.interests?.length}>
                <div class="interest-grid">
                  <For each={p.interests!}>
                    {(tag) => <span class="interest-chip selected">{tag.label}</span>}
                  </For>
                </div>
              </Show>
              <Show when={p.projects?.length}>
                <div class="profile-projects">
                  <p class="status-chip">Projects</p>
                  <ul class="project-list">
                    <For each={p.projects!}>
                      {(project) => (
                        <li>
                          <ProjectCard
                            project={project}
                            onClick={() =>
                              navigate({
                                to: "/projects/$projectId",
                                params: { projectId: project.id },
                              })
                            }
                          />
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>

              <Show when={conn()?.request_note}>
                <p class="connection-note">"{conn()!.request_note}"</p>
              </Show>

              <div class="wizard-actions">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={goDirectory}
                >
                  Back
                </button>

                <Show when={!isOwnProfile()}>
                  <Show when={!conn()}>
                    <button
                      type="button"
                      class="btn btn-primary"
                      onClick={() => {
                        track(
                          "connect_clicked",
                          { target_user_id: profileId() },
                          { ticketingEventId: eventId() || undefined },
                        );
                        setConnectOpen(true);
                      }}
                    >
                      Connect
                    </button>
                  </Show>
                  <Show when={conn()?.status === "pending" && conn()?.direction === "outgoing"}>
                    <span class="status-chip">Request sent</span>
                    <button
                      type="button"
                      class="btn btn-ghost"
                      disabled={cancelMutation.isPending}
                      onClick={() => cancelMutation.mutate(conn()!.id)}
                    >
                      Cancel request
                    </button>
                  </Show>
                  <Show when={conn()?.status === "pending" && conn()?.direction === "incoming"}>
                    <button
                      type="button"
                      class="btn btn-primary"
                      disabled={respondMutation.isPending}
                      onClick={() => respondMutation.mutate({ id: conn()!.id, action: "accept" })}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      class="btn btn-ghost"
                      disabled={respondMutation.isPending}
                      onClick={() => respondMutation.mutate({ id: conn()!.id, action: "decline" })}
                    >
                      Decline
                    </button>
                  </Show>
                  <Show when={conn()?.status === "accepted"}>
                    <span class="status-chip">Connected</span>
                  </Show>
                  <Show when={!!eventId()}>
                    <button type="button" class="btn btn-ghost" onClick={onReport}>
                      Report
                    </button>
                  </Show>
                </Show>
              </div>

              <ConnectModal
                open={connectOpen()}
                displayName={p.display_name ?? undefined}
                pending={connectMutation.isPending}
                error={connectError()}
                onClose={() => {
                  setConnectOpen(false);
                  setConnectError("");
                }}
                onSubmit={(note) => connectMutation.mutate(note)}
              />
            </>
          );
        }}
      </Show>
    </section>
  );
}